import './AppNavBar.css'
import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CONFIG } from "../../config";
import { useNavigate, useLocation } from 'react-router-dom';
import { useHelp } from '../../context/HelpContext';

import {
  Collapse,
  Navbar,
  NavbarToggler,
  Nav,
  NavItem,
  NavLink,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

import { signInWithRedirect, signOut, fetchAuthSession } from "aws-amplify/auth";
import { NavBarBrand } from "./NavBarBrand";

import { usePlayback } from "../../context/PlaybackContext";

type UserInfo = {
  name?: string;
  email?: string;
  picture?: string;
};

export const AppNavBar = ({
  additionalMenuItems
}: {
   additionalMenuItems?: (closeNavbar: () => void) => React.ReactNode;
}) => {

  const [isOpen, setIsOpen] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { dataSource, setDataSource } = usePlayback();

  const [mapProvider, setMapProvider] = useState<'mapbox' | 'google'>(() => {
    return (localStorage.getItem('roadrunner_map_provider') as 'mapbox' | 'google') || 'google';
  });

  const handleProviderChange = (newProvider: 'mapbox' | 'google') => {
    localStorage.setItem('roadrunner_map_provider', newProvider);
    setMapProvider(newProvider);

    const path = location.pathname;
    if (newProvider === 'google') {
      if (path === '/' || path === '/home') {
        navigate('/google/home');
      } else if (path === '/3d-view') {
        navigate('/google/3d-view');
      } else if (path.startsWith('/driver-view/')) {
        const vehicleId = path.split('/')[2];
        navigate(`/google/driver-view/${vehicleId}`);
      }
    } else {
      if (path === '/google/home') {
        navigate('/home');
      } else if (path === '/google/3d-view') {
        navigate('/3d-view');
      } else if (path.startsWith('/google/driver-view/')) {
        const vehicleId = path.split('/')[3];
        navigate(`/driver-view/${vehicleId}`);
      }
    }
  };

  const toggle = () => setIsOpen(!isOpen);

  const landingPageUri = CONFIG.LANDING_PAGE_URL;

  // Load auth state + user attributes
  useEffect(() => {
    let cancelled = false;

    async function loadAuth() {
      try {
        const session = await fetchAuthSession();

        const accessToken = session.tokens?.accessToken?.toString();
        const idToken = session.tokens?.idToken;
        const claims = (idToken?.payload ?? {}) as any;

        // Only treat as authenticated if we actually have tokens
        if (!accessToken || !idToken) {
          if (!cancelled) {
            setIsAuthenticated(false);
            setUser(null);
          }
          return;
        }

        const info: UserInfo = {
          name: claims?.name ?? claims?.given_name ?? claims?.email,
          email: claims?.email,
          picture: claims?.picture,
        };
        if (!cancelled) {
          setUser(info);
          setIsAuthenticated(true);
        }
      } catch  (e) {
        console.error("Error in loadAuth():", e);
        if (!cancelled) {
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    }

    loadAuth();

    return () => { cancelled = true; };
  }, []);

  const { openHelp } = useHelp();

  const logoutAndReturn = async () => {
    try {
      await signOut({ global: true });
    } finally {
      if (landingPageUri) {
        window.location.assign(landingPageUri);
      } else {
        window.location.assign("/");
      }
    }
  };

  const gotoAboutPage = useCallback(() => {
    openHelp('about');
    setIsOpen(false);
  }, [openHelp]);

  const gotoGuidePage = useCallback(() => {
    openHelp('overview');
    setIsOpen(false);
  }, [openHelp]);


  const gotoHomePage = useCallback(() => {
    if (mapProvider === 'google') {
      navigate('/google/home');
    } else {
      navigate('/home');
    }
  }, [navigate, mapProvider]);

  return (
    <div className="nav-container">
      <Navbar color="light" light expand="md">
        <NavBarBrand />
        <NavbarToggler onClick={toggle} />
        <Collapse isOpen={isOpen} navbar>
          {/* Left-aligned items */}
          <Nav className="me-auto" navbar>
            <NavItem>
              <NavLink
                to={mapProvider === 'google' ? "/google/home" : "/home"}
                id="homeBtn"
                onClick={() => gotoHomePage()}
                style={{ cursor: "pointer" }}
              >
                Home
              </NavLink>
            </NavItem>
            {additionalMenuItems && additionalMenuItems(() => setIsOpen(false))}
            <NavItem>
              <NavLink
                id="guideOverviewBtn"
                onClick={() => gotoGuidePage()}
                style={{ cursor: "pointer" }}
              >
                User Guide
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                id="aboutBtn"
                onClick={() => gotoAboutPage()}
                style={{ cursor: "pointer" }}
              >
                About
              </NavLink>
            </NavItem>
            <UncontrolledDropdown nav inNavbar>
              <DropdownToggle nav caret id="mapProviderDropDown">
                Provider: {mapProvider === 'google' ? 'Google' : 'Mapbox'}
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => handleProviderChange('mapbox')}>
                  Mapbox
                </DropdownItem>
                <DropdownItem onClick={() => handleProviderChange('google')}>
                  Google Maps
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            {window.location.hostname !== 'roadrunner-view.tarterware.com' && (
              <UncontrolledDropdown nav inNavbar>
                <DropdownToggle nav caret id="dataSourceDropDown">
                  Source: {dataSource === 'postgis' ? 'PostGIS' : 'Kafka/Redis'}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={() => setDataSource('redis_kafka')}>
                    Kafka/Redis (Real-time)
                  </DropdownItem>
                  <DropdownItem onClick={() => setDataSource('postgis')}>
                    PostGIS (Database)
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </Nav>

          {/* Right-aligned items */}
          <Nav className="ms-auto" navbar>
            {!isAuthenticated && (
              <NavItem>
                <Button
                  id="qsLoginBtn"
                  color="primary"
                  className="btn-margin"
                  onClick={() => signInWithRedirect()}
                >
                  Log in
                </Button>
              </NavItem>
            )}

            {isAuthenticated && user && (
              <UncontrolledDropdown nav inNavbar>
                <DropdownToggle nav caret id="profileDropDown">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt="Profile"
                      className="nav-user-profile rounded-circle"
                      width="50"
                    />
                  ) : (
                    <div
                      style={{
                        width: "35px",
                        height: "35px",
                        borderRadius: "50%",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#ddd",
                        color: "#333",
                        fontWeight: 600,
                      }}
                      aria-label="Profile"
                      title={user.name || user.email || "User"}
                    >
                      {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                </DropdownToggle>

                <DropdownMenu>
                  <DropdownItem header>{user.name || user.email}</DropdownItem>
                  <DropdownItem
                    to="/profile"
                    className="dropdown-profile"
                  >
                    <FontAwesomeIcon icon="user" className="mr-3" />
                    Profile
                  </DropdownItem>
                  <DropdownItem
                    id="qsLogoutBtn"
                    onClick={() => logoutAndReturn()}
                  >
                    <FontAwesomeIcon icon="power-off" className="mr-3" />
                    Logout
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </Nav>
        </Collapse>
      </Navbar>
    </div>
  );
};
